import { Box, Typography, Button, Chip, ListItem, ListItemText, ListItemIcon, List, IconButton } from '@mui/material';
import { useState, useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';

import AddItemField from 'src/widgets/AddItemField';
import TitleWithCounter from 'src/widgets/TitleWithCounter';
import Title from 'src/components/Title';
import Link from 'src/icons/Link';
import ListEntities from 'src/widgets/ListEntities';
import Dialog from 'src/icons/Dialog';
import Trash from 'src/icons/Trash';
import { CaseIcon } from 'src/icons/CaseIcon';
import useUserStore from 'src/stores/user';
import useCollectionsStore from 'src/stores/collections';
import useProjectsStore from 'src/stores/projects';
import CollectionService from 'src/services/CollectionService';
import ProjectsService from 'src/services/ProjectsService';
import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import { getBeginWorkStepByOrder } from 'src/utils/beginWork';
import { IBeginWorkOnSuccessArgs } from 'src/types';
import { ROUTES_URL } from 'src/constants/routes';

const EmptyList = () => {
    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            flexGrow={10}
        >
            <Box mb={1}>
                <Dialog />
            </Box>
            <Box mb={0.5}>
                <Typography
                    variant="body1"
                    color="primary"
                    textAlign="center"
                >
                    Добавьте чаты
                </Typography>
            </Box>
            <Box>
                <Typography
                    variant="body2"
                    color="secondary"
                    textAlign="center"
                >
                    Ссылку на чат можно взять в описании чата, для закрытов чатов скопируйте ссылку приглашения
                </Typography>
            </Box>
        </Box>
    );
};
export interface IChat {
    id: number | string;
    name: string;
}

export interface IChatsAndChannelsScreenProps {
    onSuccess?: ({ skiped }: IBeginWorkOnSuccessArgs) => void;
    /** Флаг онбординга - если true, не показываем ошибки при отсутствии проекта */
    isOnboarding?: boolean;
}

export default function ChatsAndChannelsScreen({ onSuccess, isOnboarding = false }: IChatsAndChannelsScreenProps) {
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const { privateCollection, getPrivateCollection } = useCollectionsStore(
        ({ privateCollection, getPrivateCollection }) => ({
            privateCollection,
            getPrivateCollection,
        })
    );

    const { activeProject, setActiveProjectById, setActiveProject, projects, setProjects } = useProjectsStore(
        ({ activeProject, setActiveProjectById, setActiveProject, projects, setProjects }) => ({
            activeProject,
            setActiveProjectById,
            setActiveProject,
            projects,
            setProjects,
        })
    );

    // Получаем projectId из URL
    const projectIdFromUrl = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        const param = searchParams.get('projectId');
        return param ? parseInt(param, 10) : null;
    }, [location.search]);

    // Флаг - есть ли projectId в URL
    const hasProjectId = projectIdFromUrl !== null && !isNaN(projectIdFromUrl);

    // Загружаем проекты и устанавливаем активный проект
    const [isLoadingProjects, setIsLoadingProjects] = useState(false);

    useEffect(() => {
        const loadProjects = async () => {
            // Если projectId из URL и он уже активен - ничего не делаем
            if (hasProjectId && activeProject?.id === projectIdFromUrl) {
                return;
            }

            // Если projectId из URL, но проект другой или не загружен
            if (hasProjectId) {
                const existingProject = projects.find((p) => p.id === projectIdFromUrl);
                if (existingProject) {
                    setActiveProjectById(projectIdFromUrl!);
                    return;
                }

                // Загружаем проекты
                setIsLoadingProjects(true);
                try {
                    const data = await ProjectsService.getProjects();
                    setProjects(data);
                    const targetProject = data.find((p) => p.id === projectIdFromUrl);
                    if (targetProject) {
                        setActiveProject(targetProject);
                    } else {
                        enqueueSnackbar('Проект не найден', { variant: 'error' });
                    }
                } catch {
                    enqueueSnackbar('Ошибка загрузки проектов', { variant: 'error' });
                } finally {
                    setIsLoadingProjects(false);
                }
                return;
            }

            // Онбординг режим - нет projectId в URL
            if (activeProject?.id) {
                return;
            }

            if (projects.length > 0) {
                setActiveProject(projects[0]);
                return;
            }

            // Загружаем проекты
            setIsLoadingProjects(true);
            try {
                const data = await ProjectsService.getProjects();
                setProjects(data);
                if (data.length > 0) {
                    setActiveProject(data[0]);
                }
            } catch {
                if (!isOnboarding) {
                    enqueueSnackbar('Ошибка загрузки проектов', { variant: 'error' });
                }
            } finally {
                setIsLoadingProjects(false);
            }
        };

        loadProjects();
    }, [
        hasProjectId,
        projectIdFromUrl,
        activeProject?.id,
        projects,
        setActiveProjectById,
        setActiveProject,
        setProjects,
        isOnboarding,
        enqueueSnackbar,
    ]);

    const isMobile = useIsMobileSize();
    const [items, setItems] = useState<IChat[]>([]);

    // Отладочная информация
    console.log('🔍 ChatsAndChannels Debug:', {
        activeProject,
        privateCollection,
        itemsCount: items.length,
    });

    // Загружаем личную подборку при загрузке компонента
    const {
        data: privateCollectionData,
        isLoading: isPrivateCollectionLoading,
        error: privateCollectionError,
    } = useQuery({
        queryKey: ['private-collection', activeProject?.id],
        queryFn: () => getPrivateCollection(),
        enabled: !!activeProject?.id, // Загружаем только если есть активный проект
        retry: false, // Отключаем автоматические повторные попытки
        refetchOnWindowFocus: false, // Отключаем повторную загрузку при фокусе окна
        refetchOnMount: false, // Отключаем повторную загрузку при монтировании
        refetchOnReconnect: false, // Отключаем повторную загрузку при переподключении
        staleTime: 1000 * 60 * 60 * 24, // Данные считаются свежими 24 часа
        cacheTime: 1000 * 60 * 60 * 24, // Данные хранятся в кеше 24 часа
        onSuccess: (data) => {
            const chats = data?.chats;
            if (chats) {
                setItems(
                    chats.map(({ id, telegram_link }, index) => ({
                        id: id || index,
                        name: telegram_link,
                    }))
                );
            }
        },
        onError: (error: any) => {
            console.error('❌ Ошибка загрузки личной подборки:', error);
            // Не показываем ошибку, так как getPrivateCollection уже возвращает пустую подборку при ошибке
        },
    });

    const { chatsLimit, onboardingStep } = useUserStore(({ current_subscription, onboardingStep }) => ({
        chatsLimit: current_subscription?.plan.chats_limit || 999, // Если лимит не установлен, разрешаем много чатов
        onboardingStep,
    }));

    // Подсчитываем общее количество уникальных чатов по всем личным подборкам пользователя
    // Используем Set для учета уникальных подборок, так как одна подборка может быть привязана к нескольким проектам
    const totalChatsCount = useMemo(() => {
        const uniqueBundles = new Map<number, number>(); // Map<bundleId, chat_count>

        projects.forEach((project) => {
            if ('bundles' in project && project.bundles) {
                // Проходим по всем личным подборкам в проекте
                project.bundles.forEach((bundle) => {
                    if (!bundle.is_public && bundle.id) {
                        const bundleId = bundle.id;
                        const chatCount = bundle.chat_count || 0;
                        // Сохраняем максимальное значение chat_count для каждой подборки
                        if (!uniqueBundles.has(bundleId) || uniqueBundles.get(bundleId)! < chatCount) {
                            uniqueBundles.set(bundleId, chatCount);
                        }
                    }
                });
            }
        });

        // Суммируем chat_count всех уникальных подборок
        return Array.from(uniqueBundles.values()).reduce((sum, count) => sum + count, 0);
    }, [projects]);

    // Количество чатов в текущем проекте (в форме)
    const currentProjectChatsCount = items.length;

    // Количество чатов в текущем проекте (из сохраненных данных)
    const savedCurrentProjectChatsCount = useMemo(() => {
        if (!activeProject || !('bundles' in activeProject)) return 0;
        const privateBundle = activeProject.bundles.find((bundle) => !bundle.is_public);
        return privateBundle?.chat_count || 0;
    }, [activeProject]);

    // Общее количество чатов с учетом текущего проекта
    const totalChatsWithCurrent = totalChatsCount - savedCurrentProjectChatsCount + currentProjectChatsCount;

    const isMaxCount = chatsLimit > 0 && totalChatsWithCurrent >= chatsLimit;

    const hasSkipAbility = getBeginWorkStepByOrder(onboardingStep)?.id === 'add_chats';
    const normalizeTelegramLink = (link) => {
        if (!link) return '';

        link = link.trim();

        // Если уже полная ссылка, возвращаем как есть
        if (link.startsWith('https://t.me/') || link.startsWith('http://t.me/')) {
            return link;
        }

        // Убираем @ и t.me/ префиксы
        link = link.replace(/@/, '');
        link = link.replace(/^t\.me\//, '');

        return `https://t.me/${link}`;
    };

    const onAddChat = (value: string) => {
        try {
            const link = normalizeTelegramLink(value);

            // Валидация URL
            try {
                new URL(link);
            } catch {
                enqueueSnackbar('Введите корректную ссылку на Telegram чат', { variant: 'error' });
                return;
            }

            // Проверяем, что это ссылка на Telegram
            if (!link.includes('t.me/')) {
                enqueueSnackbar('Ссылка должна быть на Telegram чат (t.me)', { variant: 'error' });
                return;
            }

            setItems((prevState) => {
                if (prevState.some(({ name }) => name === link)) {
                    enqueueSnackbar('Чат уже добавлен!', { variant: 'info' });
                    return prevState;
                }

                // Подсчитываем общее количество с учетом нового чата
                const newCurrentCount = prevState.length + 1;
                const totalWithNew = totalChatsCount - savedCurrentProjectChatsCount + newCurrentCount;

                // Проверяем общий лимит перед добавлением
                if (chatsLimit > 0 && totalWithNew > chatsLimit) {
                    const available = chatsLimit - (totalChatsCount - savedCurrentProjectChatsCount);
                    enqueueSnackbar(
                        `Достигнут лимит чатов (${chatsLimit} всего по всем проектам). Доступно для этого проекта: ${Math.max(
                            0,
                            available
                        )}`,
                        { variant: 'warning' }
                    );
                    return prevState;
                }

                return [...prevState, { name: link, id: prevState.length + 1 }];
            });
        } catch (_err) {
            enqueueSnackbar('Введите корректную ссылку', { variant: 'error' });
        }
    };

    const { mutate: updateCollection, isLoading: isUpdateCollectionLoading } = useMutation(
        CollectionService.queryKey,
        useCollectionsStore.getState().updateCollection,
        {
            onSuccess: () => {
                onSuccess?.({ skiped: false });
                // Обновляем проект после сохранения, чтобы получить актуальные данные
                if (activeProject?.id) {
                    ProjectsService.getProjects().then((data) => {
                        useProjectsStore.getState().setProjects(data);
                        useProjectsStore.getState().setActiveProjectById(activeProject.id);
                    });
                }
                enqueueSnackbar('Чаты и каналы сохранены', { variant: 'success' });
            },
            onError: (error: any) => {
                console.error('❌ Ошибка сохранения чатов:', error);
                console.error('❌ Детали ошибки:', error?.response?.data);
                console.error('❌ Статус:', error?.response?.status);

                // Показываем детали ошибки валидации
                if (error?.response?.data?.non_field_errors) {
                    console.error('❌ Ошибки валидации:', error.response.data.non_field_errors);
                }

                const errorMessage =
                    error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    error?.response?.data?.non_field_errors?.[0] ||
                    'Ошибка сохранения чатов и каналов';

                enqueueSnackbar({
                    message: errorMessage,
                    variant: 'error',
                });
            },
        }
    );

    const onDeleteChat = (chat: IChat) => {
        setItems((prevState) => prevState.filter(({ id }) => id !== chat.id));
    };

    const onSaveHandler = () => {
        if (hasSkipAbility && !currentProjectChatsCount) {
            onSuccess?.({ skiped: true });
            return;
        }

        // Проверяем общий лимит перед сохранением
        // Но разрешаем сохранение, если пользователь уменьшает количество чатов
        const newCurrentCount = items.length;
        const totalWithNew = totalChatsCount - savedCurrentProjectChatsCount + newCurrentCount;
        const isReducing = newCurrentCount <= savedCurrentProjectChatsCount;

        if (chatsLimit > 0 && totalWithNew > chatsLimit && !isReducing) {
            const available = chatsLimit - (totalChatsCount - savedCurrentProjectChatsCount);
            enqueueSnackbar(
                `Достигнут лимит чатов (${chatsLimit} всего по всем проектам). Доступно для этого проекта: ${Math.max(
                    0,
                    available
                )}`,
                { variant: 'warning' }
            );
            return;
        }

        // Используем данные из запроса или из store
        const collectionData = privateCollectionData || privateCollection;

        // Проверяем, что данные подборки существуют
        if (!collectionData || !collectionData.id) {
            enqueueSnackbar('Ошибка: личная подборка не найдена', { variant: 'error' });
            return;
        }

        const requestData = {
            id: collectionData.id,
            name: collectionData.name || 'Мои чаты',
            tags: collectionData.tags || [],
            chats: items.map(({ name }) => ({
                telegram_link: name,
                is_monitored: true,
            })),
        };

        console.log('💾 Сохраняем чаты:', {
            collectionData,
            items,
            userId: useUserStore.getState().id,
            requestData,
        });

        updateCollection(requestData);
    };

    // Если нет активного проекта, показываем сообщение
    if (!activeProject?.id) {
        return (
            <Box
                pt={2}
                minHeight="100%"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
            >
                <Typography
                    variant="h6"
                    color="error"
                    sx={{ mb: 2 }}
                >
                    Нет активного проекта
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                >
                    Для добавления чатов необходимо сначала создать проект.
                </Typography>
                {/* Создание проекта отключено */}
            </Box>
        );
    }

    // Если ошибка загрузки личной подборки, показываем сообщение
    if (privateCollectionError) {
        return (
            <Box
                pt={2}
                minHeight="100%"
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
            >
                <Typography
                    variant="h6"
                    color="error"
                    sx={{ mb: 2 }}
                >
                    Ошибка загрузки данных
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                >
                    Не удалось загрузить личную подборку. Попробуйте обновить страницу.
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => window.location.reload()}
                >
                    Обновить страницу
                </Button>
            </Box>
        );
    }

    return (
        <LoadingWrapper isLoading={isUpdateCollectionLoading || isPrivateCollectionLoading || isLoadingProjects}>
            <Box
                pt={2}
                minHeight="100%"
                display="flex"
                flexDirection="column"
            >
                <Box
                    mb={1.5}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={2}
                >
                    <Title>Чаты и группы</Title>
                    <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                    >
                        {hasProjectId && activeProject?.name && (
                            <Chip
                                icon={<CaseIcon />}
                                label={activeProject.name}
                                size="medium"
                                sx={{
                                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                                    color: 'primary.main',
                                    fontWeight: 600,
                                    border: '1px solid rgba(25, 118, 210, 0.2)',
                                    paddingLeft: '8px',
                                    paddingRight: '8px',
                                    gap: '8px',
                                    '& .MuiChip-icon': {
                                        color: 'primary.main',
                                        marginLeft: 0,
                                        marginRight: 0,
                                    },
                                }}
                            />
                        )}
                        {chatsLimit > 0 && (
                            <Chip
                                color="primary"
                                variant={totalChatsWithCurrent >= chatsLimit ? 'filled' : 'outlined'}
                                label={`${totalChatsWithCurrent} из ${chatsLimit}`}
                                sx={{
                                    background: totalChatsWithCurrent < chatsLimit && !isMobile ? '#fff' : null,
                                }}
                            />
                        )}
                    </Box>
                </Box>
                <Box
                    maxWidth="80%"
                    mb={20 / 8}
                >
                    <Typography
                        variant="body1"
                        color="primary"
                    >
                        Добавьте дополнительно известные вам чаты, где ваши потенциальные клиенты общаются и могут
                        спрашивать про ваш продукт или услугу
                    </Typography>
                </Box>

                <Box mb={3}>
                    <AddItemField
                        icon={<Link />}
                        placeholder="Добавьте ссылку на чат или группу"
                        onAddItem={onAddChat}
                        disabled={isMaxCount}
                    />
                </Box>
                {currentProjectChatsCount ? (
                    <List
                        style={{
                            border: '1px solid',
                            borderColor: '#E3ECF3',
                            borderRadius: '16px',
                            backgroundColor: '#fff',
                            width: '100%',
                        }}
                        disablePadding
                    >
                        {items.map((item, idx) => (
                            <ListItem
                                key={item.id}
                                divider={items.length - 1 !== idx}
                                secondaryAction={
                                    <IconButton
                                        edge="end"
                                        aria-label="delete"
                                        onClick={() => onDeleteChat(item)}
                                    >
                                        <Trash />
                                    </IconButton>
                                }
                            >
                                <ListItemIcon>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            '& svg': {
                                                width: '24px',
                                                height: '24px',
                                            },
                                        }}
                                    >
                                        <Dialog />
                                    </Box>
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Typography
                                            variant="body1"
                                            color="primary"
                                        >
                                            {item.name}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <EmptyList />
                )}
                <Box
                    display="flex"
                    alignItems="flex-end"
                    flexGrow={1}
                    pb={1}
                >
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        fullWidth
                        onClick={onSaveHandler}
                    >
                        {hasSkipAbility ? (currentProjectChatsCount ? 'Продолжить' : 'Пропустить') : 'Сохранить'}
                    </Button>
                </Box>
            </Box>
        </LoadingWrapper>
    );
}
